// @ts-nocheck
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import Zone from "@insite/client-framework/Components/Zone";
import translate from "@insite/client-framework/Translate";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useLocalStorage } from "@store/Framework/Common/Hooks/useLocalStorage";
import { StepForm } from "@store/Framework/Store/Bunzl/Pages/StepForm/StepFormReducer";
import { Step } from "@store/Library/Components/Bunzl/Steps/Steps";
import CardComponent from "@store/Library/Widgets/Card/Card";

import React, { createContext, useCallback, useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";

enum fields {
    numberOfTradeReferences = "numberOfTradeReferences",
    termsAndConditionsTermAccount = "termsAndConditionsTermAccount",
    termsAndConditionsCreditAccount = "termsAndConditionsCreditAccount",
    link = "link",
}
const stringRequired = (errorMessage: string) => {
    return (value: string) => (value ? undefined : errorMessage);
};
export const validationDictionary = {
    address: stringRequired(translate("Address is required")),
    contactName: stringRequired(translate("Contact Name is required")),
    name: stringRequired(translate("Name is required")),
    companyName: stringRequired(translate("Company Name is required")),
    companyAddress: (value: string) => (value ? undefined : translate("Company Address is required")),
    bankAddress: (value: string) => (value ? undefined : translate("Bank Address is required")),
    city: (value: string) => (value ? undefined : translate("City is required")),
    province: (value: string) => (value ? undefined : translate("Province is required")),
    accountNumber: (value: string) => (value ? undefined : translate("Account Number is required")),
    postalCode: (value: string) => {
        if (!value) {
            return translate("Postal Code is required");
        }
        const postalCodeRegex = /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/;
        return postalCodeRegex.test(value) ? undefined : translate("Invalid Postal Code");
    },
    phoneNumber: (value: string) => {
        if (!value) {
            return translate("Phone Number is required");
        }
        const phoneRegex = /^[+]*[(]?\d{1,4}\)?[-\s./\d]*$/;
        return phoneRegex.test(value) ? undefined : translate("Invalid Phone Number");
    },
    bankName: (value: string) => (value ? undefined : translate("Bank Name is required")),
    faxNumber: (value: string) => {
        if (!value) {
            return translate("Fax Number is required");
        }
        const faxRegex = /^[+]*[(]?\d{1,4}\)?[-\s./\d]*$/;
        const returnValue = faxRegex.test(value) ? undefined : translate("Invalid Fax Number");
        return returnValue;
    },
    businessType: (value: string) => (value ? undefined : translate("Business Type is required")),
    yearsInOperation: (value: string) => (value ? undefined : translate("Years in Operation is required")),
    email: (value: string) => {
        if (!value) {
            return translate("Email is required");
        }
        const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;
        return emailRegex.test(value) ? undefined : translate("Invalid Email Address");
    },
    title: (value: string) => (value ? undefined : translate("Title is required")),
    websiteAddress: (value: string) => {
        if (!value) {
            return undefined;
        }
        const webSiteRegex = /^(http|https):\/\/[^ "]+$/;
        return webSiteRegex.test(value) ? undefined : translate("Invalid Website Address");
    },
    gstExcempt: (value: string) => (value ? undefined : translate("GST information is required")),
    pstExcempt: (value: string) => (value ? undefined : translate("PST information is required")),
    excemptionNumber: (value: string) => (value ? undefined : translate("Excemption number is required")),
    taxExempt: (value: string) => (value ? undefined : translate("Tax information is required")),
    referenceName: stringRequired(translate("Reference Name is required")),
};
export type StepFormValidationDictionary = typeof validationDictionary;
interface CreditFormContext {
    steps: Step[];
    numberOfTradeReferences: number;
    termsAndConditionsTermAccount: string;
    termsAndConditionsCreditAccount: string;
    setSteps: (steps: Step[]) => void;
    saveForm: () => void;
    currentStep: number;
    setCurrentStep: (step: number) => void;
    storeKey: string;
    validationDictionary: typeof validationDictionary;
    isFormValid: boolean;
    link: LinkFieldValue;
}
interface CreditFormContextContainerProps extends WidgetProps {
    fields: {
        [fields.numberOfTradeReferences]: number;
        [fields.termsAndConditionsTermAccount]: string;
        [fields.termsAndConditionsCreditAccount]: string;
        [fields.link]: LinkFieldValue;
    };
}

export type CreditFormLabels =
    | "Basic Details"
    | "Principal and Tax Information"
    | "Trade References"
    | "Banking Information"
    | "Review and Submit";

interface CreditFormStep extends Step {
    label: CreditFormLabels;
}

export const initialSteps: CreditFormStep[] = [
    {
        description: "Enter Basic Details about your Business",
        form: { inputs: [] },
        label: "Basic Details",
    },
    {
        description: "Enter Tax and Principal Information",
        form: { inputs: [] },
        label: "Principal and Tax Information",
    },
    {
        label: "Trade References",
        description: "Enter Any Trade References",
        form: { inputs: [] },
    },
    {
        label: "Banking Information",
        description: "Enter Banking Information",
        form: { inputs: [] },
    },
    {
        label: "Review and Submit",
        description: "Review and Submit your application",

        form: { inputs: [] },
    },
];

export const initialFormState: StepForm = {
    steps: initialSteps.map(step => ({
        valid: false,
        label: step.label,
        description: step.description,
        inputs: {},
        percentComplete: 0,
    })),
    title: "Credit Application",
    isFormValid: false,
};

const creditFormContext = createContext<CreditFormContext | null>(null);
const storeKey = "creditForm";

const CreditFormContextContainer: React.FC<CreditFormContextContainerProps> = ({ id, fields }) => {
    const [currentStep, setCurrentStep] = React.useState(0);
    const [formInStorage] = useLocalStorage<StepForm>(storeKey);

    const [steps, setSteps] = React.useState<Step[]>(initialSteps);
    const isFormValid = useAppSelector(state => state.bunzl.pages.stepForm?.[storeKey]?.isFormValid ?? false);
    const bunzlDispatch = useDispatch();
    // placeholder for api call
    const saveForm = useCallback(() => {
        bunzlDispatch({
            type: "Data/StepForm/SaveFormToLocalStorage",
            formKey: storeKey,
        });
    }, [bunzlDispatch]);
    const handleStepChange = useMemo(
        () => (step: number) => {
            saveForm();
            setCurrentStep(step);
        },
        [saveForm],
    );

    useEffect(() => {
        bunzlDispatch({
            type: "Data/StepForm/initializeStepForm",
            formKey: storeKey,
            steps: initialFormState.steps,
            title: "Credit Application",
        });
        if (formInStorage) {
            if (initialSteps.length !== formInStorage.steps.length) {
                const newSteps: Step[] = formInStorage.steps.map(step => {
                    return {
                        ...step,
                        form: {
                            inputs: [],
                        },
                    };
                });
                setSteps(newSteps);
            }
        }
    }, [bunzlDispatch]);

    const contextValue = useMemo(
        () => ({
            steps,
            setSteps,
            currentStep,
            saveForm,
            setCurrentStep: handleStepChange,
            storeKey,
            validationDictionary,
            isFormValid,
            ...fields,
        }),
        [currentStep, fields, handleStepChange, isFormValid, saveForm, steps],
    );
    return (
        <creditFormContext.Provider value={contextValue}>
            <CardComponent variant="elevated" size="large">
                <Zone contentId={id} zoneName="Content" />
            </CardComponent>
        </creditFormContext.Provider>
    );
};

const widgetModule: WidgetModule = {
    component: CreditFormContextContainer,
    definition: {
        group: "Common",
        displayName: "Credit Form Context Container",
        fieldDefinitions: [
            {
                name: fields.numberOfTradeReferences,
                editorTemplate: "IntegerField",
                defaultValue: 3,
                displayName: "Number of Trade References",
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.termsAndConditionsTermAccount,
                editorTemplate: "RichTextField",
                defaultValue: "",
                displayName: "Terms and Conditions Trade Account",
                fieldType: "Contextual",
                sortOrder: 2,
            },
            {
                name: fields.termsAndConditionsCreditAccount,
                editorTemplate: "RichTextField",
                defaultValue: "",
                displayName: "Terms and Conditions Credit Account",
                fieldType: "Contextual",
                sortOrder: 3,
            },
            {
                name: fields.link,
                displayName: "Button Link",
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
                fieldType: "General",
            },
        ],
    },
};
const useCreditFormContext = () => {
    const context = React.useContext(creditFormContext);
    if (!context) {
        throw new Error("Credit Form Context not found");
    }
    return context;
};

export default widgetModule;

export { creditFormContext as CreditFormContext, useCreditFormContext };
